import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ACTIVITY_TYPE_LABEL,
  CASE_SORT,
  DEPARTMENT_LABEL,
  ROLE_LABEL,
  STAGE_LABEL,
  matchesSearch,
  withinRange,
  type ActivityEntry,
  type DateRange,
  type UserCaseRow,
  type SortPreset,
} from '@/domain';
import { useActivity, useUserCases, useUsers } from '@/application/usecases/useAdmin';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useSession } from '@/application/usecases/useSession';
import { formatDuration } from '@/application/timer/timerStore';
import { initials } from '@/shared/format/initials';
import { strings } from '@/shared/strings';
import { formatDay, formatInstant } from '@/shared/format/instant';
import { Button } from '@/ui/primitives/Button';
import { Tabs } from '@/ui/primitives/Tabs';
import { CaseTable } from '@/ui/data/CaseTable';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import type { Column } from '@/ui/data/ResponsiveTable';
import { SortSelect } from '@/ui/data/SortSelect';
import { StatusDot } from '@/ui/data/StatusDot';
import { GoBackIcon, KeyIcon } from '@/ui/icons';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { ChangePasswordDialog } from '@/features/auth/ChangePasswordDialog';
import { SpecialAccessCard } from './SpecialAccessCard';
import { UserNotificationSettings } from './NotificationSettings';
import styles from './UserRecordScreen.module.css';

/**
 * One person's work record. Reached by opening somebody in the team list.
 *
 * The client asked for "all the cases that user worked on, similar to the
 * archive", and this is the archive's shape applied to a person rather than to
 * a delivery: a table of work, newest first, paged.
 *
 * A row per STAGE worked, not per case. Somebody who took a case back after a
 * rework worked it twice and their record says so; collapsing the two would
 * quietly hide the rework, which is the one thing a manager reading this is
 * most likely looking for.
 *
 * There is no productivity figure here on purpose. Staff productivity is its
 * own screen with its own grant, and a record of what somebody did is not the
 * same thing as a score for how fast they did it.
 */
export function UserRecordScreen() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'cases' | 'activity'>('cases');
  const [query, setQuery] = useState('');
  const [worked, setWorked] = useState<DateRange>({ from: '', to: '' });

  const users = useUsers();
  const session = useSession();
  const mayGrant = session.data?.permissions.includes('admin') === true;
  // Your own record is the one other place your password is changed from.
  const isOwnRecord = session.data?.user.id === userId;
  const [changingPassword, setChangingPassword] = useState(false);
  /*
   * A page at a time, with the size the reader chose. Section 10 of the request
   * of 16 September 2026. This screen already counted its pages by hand, which
   * is the precedent the rest of the tables now follow.
   *
   * The filters below run in memory over the page, as they do on Orders.
   */
  const paging = useTablePage('userCases');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('userCases');
  const cases = useUserCases(userId, paging.page, paging.pageSize);

  /*
   * Everything this person did, not only the benches they worked.
   *
   * The two answer different questions and both are worth having. The case
   * table says what they made; the log says what they DID, which includes
   * dispatching an order, taking a payment or granting somebody a screen, none
   * of which leaves a row in the stage history.
   *
   * Only fetched once the tab is opened. A manager glancing at somebody's
   * cases should not pay for a log they did not ask for.
   */
  const activity = useActivity(
    tab === 'activity' && userId !== undefined ? { userId, pageSize: 100 } : { pageSize: 0 },
  );

  const user = users.data?.items.find((row) => row.id === userId);
  const rows = cases.data?.items ?? [];
  const total = cases.data?.total ?? 0;

  // A filter makes a different list, and page three of the old one is nothing
  // in the new one.
  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, query, worked]);

  const filtering = query.trim() !== '' || worked.from !== '' || worked.to !== '';
  const visible = rows.filter(
    (row) =>
      matchesSearch(query, [row.jobOrderNo, row.dentistName, row.patientName, row.product]) &&
      // The lab's day the stage was started on, not the UTC one.
      withinRange(formatDay(row.startedAt), worked),
  );

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.users.recordSearch,
      placeholder: strings.users.recordSearchPlaceholder,
      value: query,
      onChange: setQuery,
    },
    {
      kind: 'dateRange',
      label: strings.users.recordDate,
      from: worked.from,
      to: worked.to,
      onChange: setWorked,
    },
  ];

  /*
   * Most recently worked first, which is the order the server sends this
   * person's cases in and so the order the tab has always been in. A preset
   * with no comparator leaves the rows exactly as they arrived.
   */
  const presets = useMemo<SortPreset<UserCaseRow>[]>(
    () => [
      { key: 'recentlyWorked', label: strings.table.recentlyWorked },
      { key: 'newestFirst', label: strings.table.newestFirst, compare: CASE_SORT.newestFirst },
      { key: 'doctorAtoZ', label: strings.table.doctorAtoZ, compare: CASE_SORT.doctorAtoZ },
    ],
    [],
  );
  const order = useTableOrder('users:record', presets);

  const columns: Column<UserCaseRow>[] = [
    {
      key: 'product',
      label: strings.users.recordProduct,
      sortable: true,
      sortValue: (row) => row.product,
      render: (row) => (
        <>
          {row.product}
          <span className={styles.units}>
            {' '}
            · {row.units} {row.units === 1 ? strings.job.toothUnit : strings.job.toothUnits}
          </span>
        </>
      ),
    },
    {
      key: 'stage',
      label: strings.users.recordStage,
      sortable: true,
      sortValue: (row) => STAGE_LABEL[row.stage],
      render: (row) => (
        <>
          {STAGE_LABEL[row.stage]}
          {row.rework && <span className={styles.rework}> {strings.users.recordRework}</span>}
        </>
      ),
    },
    {
      key: 'activeSeconds',
      label: strings.users.recordTime,
      mono: true,
      align: 'right',
      sortable: true,
      sortValue: (row) => row.activeSeconds ?? -1,
      render: (row) =>
        // Null means the clock is still running. Showing a figure that grows
        // every second would read as a total, which it is not yet.
        row.activeSeconds === null ? (
          <span className={styles.running}>{strings.users.recordRunning}</span>
        ) : (
          formatDuration(row.activeSeconds)
        ),
    },
    {
      key: 'startedAt',
      label: strings.users.recordDate,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.startedAt,
      render: (row) => formatInstant(row.startedAt),
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    {
      // Nothing while the clock is still running, which the time beside it says.
      key: 'completedAt',
      label: strings.table.columns.completed,
      mono: true,
      nowrap: true,
      sortable: true,
      optional: true,
      sortValue: (row) => row.completedAt ?? '',
      render: (row) => (row.completedAt === null ? '' : formatInstant(row.completedAt)),
    },
  ];

  return (
    <div>
      <button className={styles.back} onClick={() => void navigate('/users')}>
        <GoBackIcon /> {strings.users.recordBack}
      </button>

      <div className={styles.head}>
        <span className={styles.avatar} aria-hidden="true">
          {initials(user?.name ?? '')}
        </span>
        <div>
          <span className={styles.lbl}>{strings.users.recordLabel}</span>
          <h2>{user?.name ?? userId}</h2>
          {user !== undefined && (
            <span className={styles.hint}>
              {user.title} · {ROLE_LABEL[user.role]} · {DEPARTMENT_LABEL[user.department]}
              {/* Section 11.2: labelled with the department it supervises. */}
              {user.isSupervisor &&
                ` · ${strings.users.supervisorOf} · ${DEPARTMENT_LABEL[user.department]}`}
            </span>
          )}
        </div>
        {isOwnRecord && (
          <Button
            className={styles.ownAction}
            icon={<KeyIcon />}
            onClick={() => setChangingPassword(true)}
          >
            {strings.password.action}
          </Button>
        )}
      </div>

      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}

      {/*
        Exceptions to this person's role, above their work: you grant access to a
        person while looking at that person. Request of 13 September 2026, 2.5.
      */}
      {mayGrant && user !== undefined && (
        <SpecialAccessCard user={user} isSelf={session.data?.user.id === user.id} />
      )}

      {/*
        What this person is notified of, where it differs from their role, with
        the way back to the role's. The request of 7 October 2026, section 1.
      */}
      {mayGrant && user !== undefined && <UserNotificationSettings userId={user.id} name={user.name} />}

      <Tabs
        label={strings.users.recordTabsLabel}
        current={tab}
        onSelect={setTab}
        tabs={[
          { key: 'cases', label: strings.users.recordTabCases, badge: total },
          { key: 'activity', label: strings.users.recordTabActivity },
        ]}
      >
      {tab === 'activity' ? (
        <UserActivity query={activity} />
      ) : (
      <>
      <div className={styles.section}>
        <h3>
          {total} {total === 1 ? strings.users.recordCasesOne : strings.users.recordCases}
        </h3>
      </div>

      {cases.isError && <p className={styles.hint}>{strings.errors.server}</p>}
      {cases.isPending && <SkeletonRows rows={4} label={strings.common.loadingCase} />}

      {!cases.isPending && !cases.isError && (
        <>
          <FilterBar
            label={strings.users.recordFiltersLabel}
            filters={filters}
            sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
          />

          <CaseTable
            customize={layout}
            columns={columns}
            rows={order.apply(visible)}
            sort={order.column}
            onSortChange={order.sortByColumn}
            identity={(row) => ({
              jobOrderNo: row.jobOrderNo,
              dentistName: row.dentistName,
              patientName: row.patientName,
            })}
            // Where the case is now: its stage, or gone.
            status={(row) =>
              row.currentStage === null ? (
                <StatusDot tone="done">{strings.users.recordGone}</StatusDot>
              ) : (
                STAGE_LABEL[row.currentStage]
              )
            }
            statusSortValue={(row) =>
              row.currentStage === null ? '' : STAGE_LABEL[row.currentStage]
            }
            rowKey={(row) => `${row.kitId}-${row.startedAt}`}
            caption={strings.users.recordCaption}
            emptyHeading={filtering ? strings.table.noMatch : strings.users.recordEmpty}
            emptyDetail={filtering ? strings.table.noMatchDetail : strings.users.recordEmptyDetail}
            onRowClick={(row) => void navigate(`/queue/${row.kitId}`)}
            paging={{
              page: paging.page,
              pageSize: paging.pageSize,
              total,
              sizes: PAGE_SIZES,
              onPage: paging.goTo,
              onPageSize: paging.choose,
              label: strings.table.pagesLabel,
            }}
          />
        </>
      )}
      </>
      )}
      </Tabs>
    </div>
  );
}

/**
 * The log for one person, in one column.
 *
 * No avatar and no name on each row, unlike the whole-building log: every row
 * here was written by the same person, and repeating their initials forty
 * times would say nothing and cost the width the action needs.
 */
function UserActivity({
  query,
}: {
  query: { data?: { items: ActivityEntry[] }; isPending: boolean; isError: boolean };
}) {
  if (query.isPending) return <SkeletonRows rows={3} label={strings.common.loadingActivity} />;
  if (query.isError) return <p className={styles.hint}>{strings.errors.server}</p>;

  const rows = query.data?.items ?? [];
  if (rows.length === 0) {
    return (
      <div className={styles.empty}>
        <h4>{strings.users.recordLogEmpty}</h4>
        <p>{strings.users.recordLogEmptyDetail}</p>
      </div>
    );
  }

  return (
    <ul className={styles.log}>
      {rows.map((entry) => (
        <li key={entry.id} className={styles.logRow}>
          <time className={styles.logWhen} dateTime={entry.createdAt}>
            {formatInstant(entry.createdAt)}
          </time>
          <div>
            <div className={styles.logLine}>
              {entry.action}{' '}
              {entry.target !== '' && <span className={styles.logTarget}>{entry.target}</span>}
            </div>
            <div className={styles.logType}>{ACTIVITY_TYPE_LABEL[entry.type]}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}
